import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { useIsMobile } from '../../../hooks';
import { MobileList } from '../../../components/common';
import api from '../../../services/api';
import { grey, success, warning } from '../../../styles/tokens';

/**
 * Liste des Programmes (Coach)
 */
const CoachPlanList = () => {
  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const [weekPlans, setWeekPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtres
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Menu actions
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  // Dialogs
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchWeekPlans();
  }, []);

  const fetchWeekPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await api.get('/coach/weekplans');
      const plans = data.plans || data || [];

      // Map client to user for backwards compatibility
      const weekPlansWithUsers = plans.map((plan) => ({
        ...plan,
        user: plan.client || plan.user,
      }));

      setWeekPlans(weekPlansWithUsers);
    } catch (err) {
      console.error('Erreur lors du chargement des programmes:', err);
      setError('Impossible de charger la liste des programmes.');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, plan) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedPlan(plan);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = async () => {
    if (!selectedPlan) return;

    try {
      setActionLoading(true);
      await api.delete(`/coach/weekplans/${selectedPlan._id}`);
      setDeleteDialog(false);
      handleMenuClose();
      fetchWeekPlans();
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      setError('Impossible de supprimer le programme.');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusChipProps = (active) => {
    return active
      ? { label: 'Actif', sx: { bgcolor: success[50], color: success.dark } }
      : { label: 'Inactif', sx: { bgcolor: grey[100], color: grey[600] } };
  };

  const getPlanStatusChipProps = (status) => {
    return status === 'published'
      ? { label: 'Publie', sx: { bgcolor: success[50], color: success.dark } }
      : { label: 'Brouillon', sx: { bgcolor: warning[50], color: warning.dark } };
  };

  // Toggle publication du plan
  const handleTogglePublish = async (planId, currentStatus) => {
    try {
      if (currentStatus === 'published') {
        // Depublier = remettre en draft
        await api.put(`/coach/weekplans/${planId}`, { status: 'draft' });
        setWeekPlans(prev => prev.map(p =>
          p._id === planId ? { ...p, status: 'draft' } : p
        ));
      } else {
        // Publier
        await api.put(`/coach/weekplans/${planId}/publish`);
        setWeekPlans(prev => prev.map(p =>
          p._id === planId ? { ...p, status: 'published' } : p
        ));
      }
    } catch (err) {
      console.error('Erreur lors du changement de statut:', err);
      setError('Erreur lors du changement de statut du plan.');
    }
  };

  // Filtrage
  const filteredPlans = weekPlans.filter((plan) => {
    const matchesFilter = statusFilter === 'all' ||
      (statusFilter === 'active' ? plan.user?.active : !plan.user?.active);
    const matchesSearch = `${plan.user?.firstName || ''} ${plan.user?.lastName || ''}`
      .toLowerCase()
      .includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <>
      <Helmet>
        <title>Programmes - Coach Yoco</title>
      </Helmet>

      <Box>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 3 }}
        >
          <Typography variant="h1">Programmes</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            component={Link}
            to="/coach/plans/create"
          >
            Ajouter
          </Button>
        </Stack>

        {/* Filtres */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <TextField
                placeholder="Rechercher par nom de client..."
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                fullWidth
                sx={{ maxWidth: { sm: 400 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: grey[400] }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Stack direction="row" spacing={1}>
                <Chip
                  label="Tous"
                  onClick={() => setStatusFilter('all')}
                  variant={statusFilter === 'all' ? 'filled' : 'outlined'}
                  color={statusFilter === 'all' ? 'primary' : 'default'}
                />
                <Chip
                  label="Actifs"
                  onClick={() => setStatusFilter('active')}
                  variant={statusFilter === 'active' ? 'filled' : 'outlined'}
                  color={statusFilter === 'active' ? 'primary' : 'default'}
                />
                <Chip
                  label="Inactifs"
                  onClick={() => setStatusFilter('inactive')}
                  variant={statusFilter === 'inactive' ? 'filled' : 'outlined'}
                  color={statusFilter === 'inactive' ? 'primary' : 'default'}
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Erreur */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Liste */}
        <Card className={isMobile ? 'mobile-list-container' : ''}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : filteredPlans.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2">
                Aucun programme trouve.
              </Typography>
            </Box>
          ) : isMobile ? (
            /* Mobile: Liste en cards */
            <Box sx={{ p: 2 }}>
              <MobileList
                items={filteredPlans}
                renderItem={(plan) => {
                  const statusProps = getStatusChipProps(plan.user?.status);
                  // V2.0: Ajouter chip warning si macros obsoletes
                  const chips = [{ label: statusProps.label, sx: statusProps.sx }];
                  if (plan.user?.macrosOutOfDate) {
                    chips.push({
                      label: 'Macros obsoletes',
                      sx: { bgcolor: warning[50], color: warning.dark },
                    });
                  }
                  return {
                    primary: plan.user ? `${plan.user.firstName} ${plan.user.lastName}` : 'Client inconnu',
                    secondary: `Semaine ${plan.weekNumber || 'N/A'}`,
                    meta: (
                      <Switch
                        size="small"
                        checked={plan.status === 'published'}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleTogglePublish(plan._id, plan.status);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        color="success"
                      />
                    ),
                    chips,
                    onClick: () => {
                      const clientId = plan.user?._id || plan.user;
                      navigate(`/coach/plans/view/${clientId}/${plan._id}`);
                    },
                  };
                }}
                emptyMessage="Aucun programme trouve"
              />
            </Box>
          ) : (
            /* Desktop: Table classique */
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell>
                    <TableCell>Semaine</TableCell>
                    <TableCell>Publie</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPlans.map((plan) => (
                    <TableRow key={plan._id} hover>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {plan.user ? `${plan.user.firstName} ${plan.user.lastName}` : 'Client inconnu'}
                          </Typography>
                          {/* V2.0: Indicateur macros obsoletes */}
                          {plan.user?.macrosOutOfDate && (
                            <WarningIcon
                              sx={{ fontSize: 16, color: warning.main }}
                              titleAccess="Macros a recalculer"
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          Semaine {plan.weekNumber || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Switch
                          size="small"
                          checked={plan.status === 'published'}
                          onChange={() => handleTogglePublish(plan._id, plan.status)}
                          color="success"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1">
                          {new Date(plan.createdAt).toLocaleDateString('fr-FR')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, plan)}
                        >
                          <MoreVertIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>

        {/* Menu actions */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() => {
              const clientId = selectedPlan?.user?._id || selectedPlan?.user;
              navigate(`/coach/plans/view/${clientId}/${selectedPlan?._id}`);
              handleMenuClose();
            }}
          >
            <VisibilityIcon sx={{ fontSize: 18, mr: 1.5 }} />
            Voir details
          </MenuItem>

          <MenuItem
            onClick={() => {
              const clientId = selectedPlan?.user?._id || selectedPlan?.user;
              navigate(`/coach/plans/edit/${clientId}/${selectedPlan?._id}`);
              handleMenuClose();
            }}
          >
            <EditIcon sx={{ fontSize: 18, mr: 1.5 }} />
            Modifier
          </MenuItem>

          <MenuItem onClick={() => setDeleteDialog(true)} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ fontSize: 18, mr: 1.5 }} />
            Supprimer
          </MenuItem>
        </Menu>

        {/* Dialog Suppression */}
        <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
          <DialogTitle>Supprimer le programme ?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Etes-vous sur de vouloir supprimer definitivement ce programme ?
              <br /><br />
              <strong>Cette action est irreversible.</strong>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog(false)} variant="outlined">
              Annuler
            </Button>
            <Button onClick={handleDelete} color="error" variant="contained" disabled={actionLoading}>
              {actionLoading ? <CircularProgress size={20} /> : 'Supprimer'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default CoachPlanList;
